import { components } from 'be-types';
import { ClosedTaskSetTask } from 'src/services/my-tasks.service';
import { Category } from './category.model';
import { TableTag } from './document.model';
import { DataScope } from './global-enum';
import { ApiRequestParams } from './global-interfaces';
import { ProfileCred, ProfileResource, ProfileSummary } from './role.model';
import { TaskSetTask, AssignableTaskResource, ClosedTaskResponse } from './task.model';
import { RoleUserResource, UserMinimum, RoleResource, TaskUserPreview } from './user.model';

export const taskSetTableApiRequestParams: ApiRequestParams = {
  dataScope: DataScope.TaskSetTable,
};

export type CopyTaskSetRequest = components['schemas']['CopyTaskSetRequest'];
export type SpawnTaskSetRequest = components['schemas']['SpawnTaskSetRequest'];
export type TaskSetRecallRequest = components['schemas']['TaskSetRecallRequest'];
export type ReassignTaskSetRequest = components['schemas']['ReassignTaskSetRequest'];
export type DeployTaskSetRequest = components['schemas']['DeployTaskSetRequest'];
export type TaskSetNameAdditionRequest = components['schemas']['UpdateTaskNameAdditionRequest'];

export interface TaskSetMinimum {
  id: number;
  task_set_name?: string;
}

export interface TaskSetBase {
  id: number;
  name_addition?: string;
  task_set_name?: string;
}

export interface TaskSetInfo extends TaskSetBase {
  topic?: Category;
  default_assignee?: RoleUserResource | ProfileCred;
  created_at?: string;
  completed_at?: string;
  created_by?: string;
  private_notes?: string;
  public_notes?: string;
  is_completed?: boolean;
  is_deployed?: boolean;
  is_spawned?: boolean;
  is_failed?: boolean;
  is_global?: boolean;
  summary?: string;
  color?: number;
  timezone_abbreviation?: string;
}

export interface EditTaskSet extends TaskSetBase {
  roles: ProfileResource[];
  topics: Category[];
  topic: Category;
  tags: TableTag[];
  tasks: TaskSetTask[];
  profile: (ProfileResource | RoleUserResource)[];
  availableTags: TableTag[];
  availableTasks: AssignableTaskResource[];
  type: number;
}

export type UpdateTaskSetRequest = components['schemas']['UpdateTaskSetRequest'];

export interface TaskSetOptions extends TaskSetBase {
  deployed_locations?: DeployedLocation[];
  duration: UpdateTaskSetRequest['duration'];
  turnGreen: number;
  turnYellow: number;
  turnRed: number;
  notify_on_assignment: number;
  notify_on_yellow: number;
  notify_on_red: number;
  notify_on_completion: number;
  task_reoccurs?: TaskSetReoccurs[];
  deploy_status: boolean;
  spawn_status: boolean;
  message?: string;
  allow_events?: boolean;
  allow_assessment?: boolean;
  is_deployed?: boolean;
  timezone_abbreviation?: string;
  default_assignee: (UserMinimum | RoleResource)[];
  due_at: string;
  location_id: number;
  roles: ProfileSummary[];
  users: TaskUserPreview[];
}

export interface DeployedLocation {
  id?: number;
  locationName?: string;
  created_at?: string;
}

export interface TaskSetReoccurs {
  name?: string;
  id?: number;
  type?: number;
  startAt?: string;
  field1?: number;
  field2?: string;
  field3?: string;
  field4?: string;
  dueAt?: string;
  reoccurAt?: string;
  failedTasksEnabled?: number;
  failedTasksSchedule?: {
    id?: number;
    duration?: number;
    new_tasks?: number;
    time_interval?: string;
  };
}

export interface TaskSetTableRecord {
  task_set_name: string;
  id: number;
  topic: Category;
  type: number;
  name_addition: string;
  default_assignee: (UserMinimum | RoleResource)[];
}

export interface ClosedTaskSetResponse {
  id: number;
  nameAddition: string;
  taskSetName: string;
  completedAt: string;
  type: number;
  topic: Category;
  closedTaskSets: ClosedTaskSet[];
  timezone_abbreviation: string;
}

export interface ClosedTaskSet {
  id: number;
  nameAddition: string | null;
  taskSetName: string;
  completedAt: string;
  type: number;
  dueAt: string;
  tasks: ClosedTaskSetTask[];
  topic: Category;
  closedTaskSets: ClosedTaskResponse[];
  timezone_abbreviation: string;
  color: number;
  tags: TableTag[];
}
